import type { FC, ReactNode, RefObject } from 'react'
import type { MotionValue } from 'motion/react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

import { animate, motion, useInView, useMotionValue } from 'motion/react'
import { easeOutQuint } from '@/lib/motion'

export type ToneMode = 'dark' | 'light'

interface ToneContextValue {
    background: MotionValue<string>
    mode: ToneMode
    setTone: (color: string, mode: ToneMode) => void
    setMode: (mode: ToneMode) => void
}

interface ToneProviderProps {
    children: ReactNode
}

const ToneContext = createContext<ToneContextValue | null>(null)

export const ToneProvider: FC<ToneProviderProps> = ({ children }): ReactNode => {
    const background = useMotionValue('#0e0e0b')
    const [mode, setModeState] = useState<ToneMode>('dark')

    const setTone = useCallback((color: string, nextMode: ToneMode) => {
        animate(background, color, { duration: 0.9, ease: easeOutQuint })
        setModeState(nextMode)
    }, [background])

    const setMode = useCallback((nextMode: ToneMode) => {
        setModeState(nextMode)
    }, [])

    const value = useMemo(() => ({
        background,
        mode,
        setTone,
        setMode
    }), [background, mode, setTone, setMode])

    return (
        <ToneContext.Provider value={value}>
            <motion.div
                aria-hidden='true'
                className='pointer-events-none fixed inset-0 -z-10'
                style={{ backgroundColor: background }}
            />

            {children}
        </ToneContext.Provider>
    )
}

export const useTone = (): ToneContextValue => {
    const context = useContext(ToneContext)

    if (!context) {
        throw new Error('useTone must be used inside ToneProvider')
    }

    return context
}

export const useSectionTone = (ref: RefObject<HTMLElement | null>, color: string, mode: ToneMode): void => {
    const { setTone } = useTone()
    const crossing = useInView(ref, { margin: '-50% 0px -50% 0px' })

    useEffect(() => {
        if (crossing) setTone(color, mode)
    }, [crossing, color, mode, setTone])
}

export const usePageTone = (color: string, mode: ToneMode): void => {
    const { setTone } = useTone()

    useEffect(() => {
        setTone(color, mode)
    }, [color, mode, setTone])
}
